import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  components as selectComponents,
  type GroupBase,
  type InputProps,
  type MultiValue,
  type OptionProps,
  type SingleValue,
  type SingleValueProps,
} from 'react-select';
import { AsyncPaginate, type LoadOptions } from 'react-select-async-paginate';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { DataTableDirection, DataTableMetadataColumn } from '@/components/UI/DataTableV2/DataTable.model';
import {
  isMultiSelectFieldType,
  parsePaginationFromUrl,
  parseSelectValue,
} from '@/components/UI/DataTableV2/DataTable.util';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import type { SubEditorProps } from '@/components/UI/DataTableV2/SingleCellEditor/SingleCellEditor';
import { ApiService } from '@/support/ApiService';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import styles from './SelectCellEditor.module.scss';

interface SelectOption {
  value: string;
  label: string;
}

const INITIAL_PAGINATION = { page: 1, perPage: 100 } as const;

/**
 * Renders a select option with a stable `data-testid` derived from its value,
 * so tests can target options via test attributes instead of visible text.
 */
const OptionWithTestId: FC<OptionProps<SelectOption, boolean, GroupBase<SelectOption>>> = (props) => (
  <selectComponents.Option {...props}>
    <span data-testid={`select-cell-editor-option-${props.data.value}`}>{props.children}</span>
  </selectComponents.Option>
);

/**
 * Renders the select's text input with a stable `data-testid` so tests can
 * target the combobox via test attributes instead of its ARIA role.
 */
const InputWithTestId: FC<InputProps<SelectOption, boolean, GroupBase<SelectOption>>> = (props) => (
  <selectComponents.Input {...props} data-testid="select-cell-editor-input" />
);

/**
 * Renders the selected single value with a stable `data-testid` so tests can
 * assert the seeded selection via test attributes instead of visible text.
 */
const SingleValueWithTestId: FC<SingleValueProps<SelectOption, boolean, GroupBase<SelectOption>>> = (props) => (
  <selectComponents.SingleValue {...props}>
    <span data-testid={`select-cell-editor-value-${props.data.value}`}>{props.children}</span>
  </selectComponents.SingleValue>
);

const SELECT_COMPONENTS = {
  DropdownIndicator: null,
  IndicatorSeparator: null,
  Option: OptionWithTestId,
  Input: InputWithTestId,
  SingleValue: SingleValueWithTestId,
} as const;

/**
 * Async paginated select editor for metadata select / multi_select columns.
 *
 * Mounted by the shell only while the cell is active. Seeds its selection from
 * the current cell value, loads options from the glossary options endpoint, and
 * commits through the shell's `onCommit`/`onCancel`:
 * - single-select commits immediately on change, moving the selection when the
 *   change was triggered by Enter/Shift+Enter,
 * - multi-select keeps the menu open and commits on Enter (with move) or on
 *   click-outside/Escape (no move).
 * Commits are suppressed to `onCancel` when the value is unchanged, preserving
 * the old "only write if changed" behaviour. A `finishedRef` guards the
 * escape/blur/click-outside race so the value is committed exactly once.
 */
export const SelectCellEditor: FC<SubEditorProps> = ({ idCoordinate, indexCoordinate, onCommit, onCancel }) => {
  const { dataTableService } = useDataTable();
  const containerRef = useRef<HTMLDivElement>(null);
  const finishedRef = useRef(false);
  const keyboardNavigationDirection = useRef<DataTableDirection | null>(null);
  const { newAbortController } = useAbortController();

  const column = dataTableService.columnByIndex(indexCoordinate.column) as DataTableMetadataColumn | undefined;
  const glossaryApiId = column?.metadata?.metadata_glossary_api_id;
  const isMulti = isMultiSelectFieldType(column?.metadata?.field_type);

  const [cellValue] = useState<string | null>(() => dataTableService.cellData(idCoordinate)?.value ?? null);

  const [selectedOptions, setSelectedOptions] = useState<Array<SelectOption>>(() =>
    parseSelectValue(cellValue, glossaryApiId).map((value) => ({ value, label: value }))
  );
  const selectedOptionsRef = useRef<Array<SelectOption>>(selectedOptions);

  const loadOptions: LoadOptions<
    SelectOption,
    GroupBase<SelectOption>,
    { page: number; perPage: number }
  > = useCallback(
    async (query, _loadedOptions, additional) => {
      try {
        const page = additional?.page ?? 1;
        const perPage = additional?.perPage ?? 100;

        if (_isNil(glossaryApiId)) {
          return { options: [], hasMore: false, additional: { page: 1, perPage: 100 } };
        }

        const trimmedQuery = query.trim();
        const queryParams: Record<string, string> = {
          page: String(page),
          perPage: String(perPage),
        };

        if (_isNotEmpty(trimmedQuery)) {
          queryParams.search = trimmedQuery;
        }

        const response = await ApiService.call({
          endpoint: 'GET /api/v1/metadata-glossary/{metadataGlossaryApiId}/options',
          path: { metadataGlossaryApiId: glossaryApiId },
          query: queryParams,
          signal: newAbortController().signal,
          options: {
            onError: {
              slug: 'datatable-select-options-load',
              toast: true,
              throw: true,
              capture: false,
            },
          },
        });

        const { data, links } = response.body!;
        const nextLink = links?.next;
        const hasMore = _notNil(nextLink);

        const options: Array<SelectOption> = data.map((item: SelectOption) => ({
          value: item.label,
          label: item.label,
        }));

        const pagination = hasMore ? parsePaginationFromUrl(nextLink, { page: page + 1, perPage }) : { page, perPage };

        return {
          options,
          hasMore,
          additional: pagination,
        };
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to load metadata options', {
              cause: error,
              slug: 'datatable-select-options-load',
              level: 'warning',
              context: {
                ...(_notNil(glossaryApiId) && { metadataGlossaryApiId: glossaryApiId }),
                query: query.trim(),
              },
            })
          );
        }
        return { options: [], hasMore: false, additional: { page: 1, perPage: 100 } };
      }
    },
    [glossaryApiId, newAbortController]
  );

  const commit = useCallback(
    (values: Array<SelectOption>, moveDirection?: DataTableDirection): void => {
      const newValue = _isNotEmpty(values) ? JSON.stringify(values.map((option) => option.value)) : null;

      if (newValue === cellValue) {
        onCancel();
        return;
      }

      onCommit(newValue, _notNil(moveDirection) ? { moveSelection: moveDirection } : undefined);
    },
    [cellValue, onCommit, onCancel]
  );

  const handleChange = useCallback(
    (newValue: MultiValue<SelectOption> | SingleValue<SelectOption>): void => {
      if (finishedRef.current) {
        return;
      }

      const values: Array<SelectOption> = isMulti
        ? Array.isArray(newValue)
          ? [...newValue]
          : []
        : _notNil(newValue)
          ? [newValue as SelectOption]
          : [];

      setSelectedOptions(values);
      selectedOptionsRef.current = values;

      if (!isMulti) {
        finishedRef.current = true;
        const direction = keyboardNavigationDirection.current;
        keyboardNavigationDirection.current = null;
        commit(values, direction ?? undefined);
      }
    },
    [isMulti, commit]
  );

  const handleSelectKeyDown = useCallback((event: React.KeyboardEvent): void => {
    if (event.code === 'Enter') {
      keyboardNavigationDirection.current = event.shiftKey ? 'UP' : 'DOWN';
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const handleClickOutside = (event: MouseEvent): void => {
      if (finishedRef.current) {
        return;
      }
      if (_notNil(containerRef.current) && !containerRef.current.contains(event.target as Node)) {
        finishedRef.current = true;
        commit(selectedOptionsRef.current);
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (finishedRef.current) {
        return;
      }
      if (event.code === 'Escape') {
        finishedRef.current = true;
        commit(selectedOptionsRef.current);
      } else if (event.code === 'Enter' && isMulti) {
        finishedRef.current = true;
        commit(selectedOptionsRef.current, event.shiftKey ? 'UP' : 'DOWN');
      }
    };

    window.addEventListener('mousedown', handleClickOutside, { signal, capture: true });
    window.addEventListener('keydown', handleKeyDown, { signal });

    return () => {
      abortController.abort();
    };
  }, [commit, isMulti]);

  return (
    <div ref={containerRef} className={styles.container} data-testid="select-cell-editor">
      <AsyncPaginate
        value={isMulti ? selectedOptions : (selectedOptions[0] ?? null)}
        loadOptions={loadOptions}
        onChange={handleChange}
        onKeyDown={handleSelectKeyDown}
        additional={INITIAL_PAGINATION}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        autoFocus
        defaultOptions
        menuIsOpen
        debounceTimeout={300}
        classNamePrefix="datatable-select"
        placeholder="Search..."
        maxMenuHeight={300}
        components={SELECT_COMPONENTS}
      />
    </div>
  );
};
